<template>
  <div>
    <!-- Header with Filters -->
    <v-card class="mb-3 filter-card">
      <v-card-title class="filter-card-title">
        <v-icon left>mdi-history</v-icon>
        {{ $t('billing.history.title') }}
        <v-spacer />
        <v-btn
          color="primary"
          small
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('billing.actions.refresh') }}
        </v-btn>
      </v-card-title>

      <v-card-text class="filter-card-text">
        <!-- Filters -->
        <v-row dense align="end">
          <v-col cols="12" md="3">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartDate"
                  :label="$t('billing.history.startDate')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  hide-details
                  @click:clear="filters.startDate = null"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedEndDate"
                  :label="$t('billing.history.endDate')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  hide-details
                  @click:clear="filters.endDate = null"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.varianceStatus"
              :items="varianceStatusOptions"
              :label="$t('billing.history.varianceStatus')"
              prepend-icon="mdi-compare"
              clearable
              dense
              hide-details
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.minAmount"
              :label="$t('billing.history.minAmount')"
              type="number"
              prepend-icon="mdi-currency-usd"
              clearable
              dense
              hide-details
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.maxAmount"
              :label="$t('billing.history.maxAmount')"
              type="number"
              prepend-icon="mdi-currency-usd"
              clearable
              dense
              hide-details
            />
          </v-col>
        </v-row>

        <!-- Search Bar -->
        <v-row dense align="center" class="mt-3">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchText"
              :label="$t('billing.history.searchRecords')"
              prepend-icon="mdi-magnify"
              clearable
              outlined
              dense
              hide-details
              :placeholder="$t('billing.history.searchPlaceholder')"
              @input="performSearch"
            />
          </v-col>
          <v-col cols="12" md="4" class="filter-actions">
            <v-btn
              color="success"
              small
              @click="exportAllRecords"
              :disabled="filteredBillingRecords.length === 0"
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t('billing.actions.exportAll') }}
            </v-btn>
            <v-btn
              color="info"
              class="ml-2"
              small
              @click="exportSummary"
              :disabled="filteredBillingRecords.length === 0"
            >
              <v-icon left>mdi-chart-line</v-icon>
              {{ $t('billing.actions.summary') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" class="text-right">
            <v-btn
              color="grey"
              text
              small
              @click="clearFilters"
            >
              {{ $t('billing.actions.clearFilters') }}
            </v-btn>
            <v-btn
              color="primary"
              class="ml-2"
              small
              @click="applyFilters"
            >
              {{ $t('billing.actions.applyFilters') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredBillingRecords"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100]
      }"
      class="elevation-1"
      sort-by="createdAt"
      sort-desc
    >
      <!-- Billing Period Column -->
      <template #item.billingPeriod="{ item }">
        <div>
          <div class="text-subtitle-2">{{ formatBillingPeriod(item.billingPeriod) }}</div>
          <div class="text-caption grey--text">
            {{ getBillingDuration(item.billingPeriod) }}
            {{ $t('billing.history.days') }}
          </div>
        </div>
      </template>

      <!-- Total Consumption Column -->
      <template #item.totalConsumption="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">
            {{ formatNumber(item.consumptionData.totalConsumption) }}
          </div>
          <div class="text-caption grey--text">kWh</div>
        </div>
      </template>

      <!-- Actual kWh from Bill Column -->
      <template #item.actualKwhFromBill="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">{{ formatNumber(item.actualKwhFromBill || 0) }}</div>
          <div class="text-caption grey--text">kWh</div>
        </div>
      </template>

      <!-- kVA Demand Column -->
      <template #item.kvaDemand="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">{{ formatNumber(getDisplayDemand(item)) }}</div>
          <div class="text-caption grey--text">{{ getDemandUnit(item) }}</div>
        </div>
      </template>

      <!-- Calculated Amount Column -->
      <template #item.calculatedAmount="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2 primary--text">
            {{ formatCurrency(getCalculatedTotal(item)) }}
          </div>
          <div class="text-caption grey--text">{{$currencySymbol}}</div>
        </div>
      </template>

      <!-- Actual Amount Column -->
      <template #item.actualAmount="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2 secondary--text">
            {{ formatCurrency(item.actualBillAmount) }}
          </div>
          <div class="text-caption grey--text">{{$currencySymbol}}</div>
        </div>
      </template>

      <!-- Per Unit Charge Column -->
      <template #item.perUnitCharge="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2 info--text">
            {{ formatNumber(getPerUnitCharge(item), 2) }}
          </div>
          <div class="text-caption grey--text">{{$currencySymbol}}/kWh</div>
        </div>
      </template>

      <!-- Variance Column -->
      <template #item.variance="{ item }">
        <div class="text-right">
          <v-chip
            small
            :color="getVarianceColor(item.variance, getCalculatedTotal(item))"
            :text-color="getVarianceTextColor(item.variance, getCalculatedTotal(item))"
          >
            <v-icon left small>{{ getVarianceIcon(item.variance) }}</v-icon>
            {{ formatCurrency(Math.abs(item.variance)) }}
          </v-chip>
          <div class="text-caption grey--text">
            {{ getVariancePercentage(item.variance, getCalculatedTotal(item)) }}%
          </div>
        </div>
      </template>

      <!-- Created Date Column -->
      <template #item.createdAt="{ item }">
        <div>
          <div class="text-subtitle-2">{{ formatDate(item.createdAt) }}</div>
          <div class="text-caption grey--text">{{ formatTime(item.createdAt) }}</div>
        </div>
      </template>

      <!-- Actions Column -->
      <template #item.actions="{ item }">
        <div class="table-action-group">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                class="table-action-btn"
                :aria-label="$t('billing.actions.view')"
                v-bind="attrs"
                v-on="on"
                @click="viewBill(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('billing.actions.view') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="warning"
                class="table-action-btn"
                :aria-label="$t('billing.actions.edit')"
                v-bind="attrs"
                v-on="on"
                @click="editBill(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('billing.actions.edit') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                class="table-action-btn"
                aria-label="Delete"
                v-bind="attrs"
                v-on="on"
                @click="confirmDeleteBill(item)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </div>
      </template>

      <!-- No Data -->
      <template #no-data>
        <div class="text-center pa-4">
          <v-icon size="64" color="grey">mdi-database-off</v-icon>
          <div class="text-h6 grey--text mt-2">{{ $t('billing.history.noRecords') }}</div>
          <div class="text-body-2 grey--text">
            {{ hasActiveFilters
              ? $t('billing.history.adjustFilters')
              : $t('billing.history.createFirstRecord') }}
          </div>
          <v-btn
            v-if="!hasActiveFilters"
            color="primary"
            class="mt-4"
            @click="$router.push('/billing-entry')"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('billing.actions.createNewBill') }}
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Summary Statistics -->
    <v-card class="mt-4" v-if="filteredBillingRecords.length > 0">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-chart-box</v-icon>
        {{ $t('billing.history.summaryStatistics') }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 primary--text">{{ filteredBillingRecords.length }}</div>
                <div class="text-subtitle-2">{{ $t('billing.history.totalRecords') }}</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 success--text">{{ formatCurrency(totalCalculatedAmount) }}</div>
                <div class="text-subtitle-2">{{ $t('billing.history.totalCalculated') }}</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 secondary--text">{{ formatCurrency(totalActualAmount) }}</div>
                <div class="text-subtitle-2">{{ $t('billing.history.totalActual') }}</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4" :class="totalVarianceColor">
                  {{ formatCurrency(Math.abs(totalVariance)) }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.history.totalVariance') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showDeleteDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="error--text">
          <v-icon left color="error">mdi-alert</v-icon>
          Delete Billing Record
        </v-card-title>
        <v-card-text>
          <div v-if="selectedRecord">
            Are you sure you want to delete billing record
            <strong>{{ selectedRecord._id || selectedRecord.id }}</strong>?
          </div>
          <div class="mt-2">
            This action will remove the record from billing history.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deletingRecord" @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingRecord"
            @click="deleteBillConfirmed"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ExportService, SearchService } from '../../services/exportService';

export default {
  name: 'BillingList',

  data() {
    return {
      itemsPerPage: 25,
      startDateMenu: false,
      endDateMenu: false,
      searchText: '',
      searchResults: [],
      filters: {
        startDate: null,
        endDate: null,
        varianceStatus: null,
        minAmount: null,
        maxAmount: null,
      },
      appliedFilters: {
        startDate: null,
        endDate: null,
        varianceStatus: null,
        minAmount: null,
        maxAmount: null,
      },
      showDeleteDialog: false,
      selectedRecord: null,
      deletingRecord: false,
    };
  },

  computed: {
    ...mapGetters('billing', ['allBillingRecords', 'loading']),

    billingRecords() {
      return this.allBillingRecords || [];
    },

    varianceStatusOptions() {
      return [
        { text: this.$t('billing.variance.all'), value: null },
        { text: this.$t('billing.variance.perfect'), value: 'perfect' },
        { text: this.$t('billing.variance.acceptable'), value: 'acceptable' },
        { text: this.$t('billing.variance.significant'), value: 'significant' },
      ];
    },

    headers() {
      return [
        {
          text: this.$t('billing.history.billingPeriod'),
          value: 'billingPeriod',
          sortable: false,
        },
        {
          text: this.$t('billing.history.consumption'),
          value: 'totalConsumption',
          sortable: true,
          align: 'right',
        },
        {
          text: this.$t('billing.history.actualKwhFromBill'),
          value: 'actualKwhFromBill',
          sortable: true,
          align: 'right',
        },
        {
          text: 'Demand',
          value: 'kvaDemand',
          sortable: true,
          align: 'right',
        },
        {
          text: this.$t('billing.history.calculatedAmount'),
          value: 'calculatedAmount',
          sortable: true,
          align: 'right',
        },
        {
          text: this.$t('billing.history.actualAmount'),
          value: 'actualAmount',
          sortable: true,
          align: 'right',
        },
        {
          text: this.$t('billing.history.perUnitCharge'),
          value: 'perUnitCharge',
          sortable: true,
          align: 'right',
        },
        {
          text: this.$t('billing.history.variance'),
          value: 'variance',
          sortable: true,
          align: 'right',
        },
        { text: this.$t('billing.history.created'), value: 'createdAt', sortable: true },
        {
          text: this.$t('billing.history.actions'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '132px',
        },
      ];
    },

    filteredBillingRecords() {
      // Start with search results if search is active, otherwise use all records
      let filtered = this.searchText ? this.searchResults : [...this.billingRecords];

      // Apply additional filters
      const searchCriteria = {
        ...this.appliedFilters,
        searchText: this.searchText,
      };

      // Use SearchService for consistent filtering
      filtered = SearchService.searchRecords(filtered, searchCriteria);

      return filtered;
    },

    formattedStartDate() {
      return this.filters.startDate ? this.formatDate(this.filters.startDate) : '';
    },

    formattedEndDate() {
      return this.filters.endDate ? this.formatDate(this.filters.endDate) : '';
    },

    hasActiveFilters() {
      return Object.values(this.appliedFilters).some((value) => value !== null && value !== '');
    },

    // Summary statistics
    totalCalculatedAmount() {
      return this.filteredBillingRecords.reduce(
        (sum, record) => sum + this.getCalculatedTotal(record),
        0,
      );
    },

    totalActualAmount() {
      return this.filteredBillingRecords.reduce((sum, record) => sum + record.actualBillAmount, 0);
    },

    totalVariance() {
      return this.totalActualAmount - this.totalCalculatedAmount;
    },

    totalVarianceColor() {
      return this.totalVariance >= 0 ? 'error--text' : 'warning--text';
    },
  },

  async created() {
    await this.refreshData();
  },

  methods: {
    ...mapActions('billing', ['fetchBillingRecords', 'deleteBillingRecord']),

    async refreshData() {
      try {
        await this.fetchBillingRecords();
      } catch (error) {
        console.error('Error fetching billing records:', error);
      }
    },

    applyFilters() {
      this.appliedFilters = { ...this.filters };
    },

    clearFilters() {
      this.filters = {
        startDate: null,
        endDate: null,
        varianceStatus: null,
        minAmount: null,
        maxAmount: null,
      };
      this.appliedFilters = { ...this.filters };
      this.searchText = '';
      this.searchResults = [];
    },

    viewBill(bill) {
      const billId = bill._id || bill.id;
      this.$router.push(`/billing-history/${billId}`);
    },

    editBill(bill) {
      const billId = bill._id || bill.id;
      this.$router.push(`/billing-history/${billId}/edit`);
    },

    confirmDeleteBill(bill) {
      this.selectedRecord = bill;
      this.showDeleteDialog = true;
    },

    async deleteBillConfirmed() {
      if (!this.selectedRecord) {
        return;
      }

      this.deletingRecord = true;
      try {
        const recordId = this.selectedRecord._id || this.selectedRecord.id;
        await this.deleteBillingRecord(recordId);
        this.showDeleteDialog = false;
        this.selectedRecord = null;
        this.$emit('show-message', {
          type: 'success',
          message: 'Billing record deleted successfully',
        });
      } catch (error) {
        this.$emit('show-message', {
          type: 'error',
          message: error.message || 'Failed to delete billing record',
        });
      } finally {
        this.deletingRecord = false;
      }
    },

    exportAllRecords() {
      try {
        const recordsToExport = this.filteredBillingRecords;
        if (recordsToExport.length === 0) {
          this.$emit('show-message', {
            type: 'warning',
            message: this.$t('billing.messages.noRecordsToExport'),
          });
          return;
        }

        const filename = `billing_records_${new Date().toISOString().split('T')[0]}.csv`;
        ExportService.exportToCSV(recordsToExport, filename);

        this.$emit('show-message', {
          type: 'success',
          message: this.$t('billing.messages.recordsExported', {
            count: recordsToExport.length,
          }),
        });
      } catch (error) {
        console.error('Error exporting records:', error);
        this.$emit('show-message', {
          type: 'error',
          message: this.$t('billing.messages.recordsExportFailed'),
        });
      }
    },

    exportSummary() {
      try {
        const recordsToExport = this.filteredBillingRecords;
        if (recordsToExport.length === 0) {
          this.$emit('show-message', {
            type: 'warning',
            message: this.$t('billing.messages.noRecordsToExport'),
          });
          return;
        }

        const filename = `billing_summary_${new Date().toISOString().split('T')[0]}.csv`;
        ExportService.exportSummaryToCSV(recordsToExport, filename);

        this.$emit('show-message', {
          type: 'success',
          message: this.$t('billing.messages.summaryExported'),
        });
      } catch (error) {
        console.error('Error exporting summary:', error);
        this.$emit('show-message', {
          type: 'error',
          message: this.$t('billing.messages.summaryExportFailed'),
        });
      }
    },

    performSearch() {
      if (!this.searchText || this.searchText.trim() === '') {
        this.searchResults = [];
        return;
      }

      // Perform search using SearchService
      const searchCriteria = {
        searchText: this.searchText.trim(),
      };

      this.searchResults = SearchService.searchRecords(this.billingRecords, searchCriteria);
    },

    // Formatting methods
    formatBillingPeriod(period) {
      const start = new Date(period.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(period.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} - ${end}`;
    },

    getBillingDuration(period) {
      const start = new Date(period.startDate);
      const end = new Date(period.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    formatNumber(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatCurrency(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString();
    },

    // Variance methods
    getVariancePercentageValue(variance, calculatedAmount) {
      if (!calculatedAmount) return 0;
      return Math.abs((variance / calculatedAmount) * 100);
    },

    getVariancePercentage(variance, calculatedAmount) {
      return this.getVariancePercentageValue(variance, calculatedAmount).toFixed(1);
    },

    getVarianceColor(variance, calculatedAmount) {
      const percentage = this.getVariancePercentageValue(variance, calculatedAmount);
      if (percentage < 1) return 'success';
      if (percentage <= 5) return 'warning';
      return 'error';
    },

    getVarianceTextColor(variance, calculatedAmount) {
      const percentage = this.getVariancePercentageValue(variance, calculatedAmount);
      if (percentage < 1) return 'white';
      return 'white';
    },

    getVarianceIcon(variance) {
      return variance >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    getDemandBreakdown(record = {}) {
      return record.timeOfUseDemand
        || record.consumptionData?.demandBreakdown
        || {};
    },

    isTimeOfUseDemandRecord(record = {}) {
      return Object.values(this.getDemandBreakdown(record)).some((line) => (
        (parseFloat(line?.kw) || 0) > 0
      ));
    },

    getDisplayDemand(record = {}) {
      if (!this.isTimeOfUseDemandRecord(record)) {
        return parseFloat(record.kvaDemand) || 0;
      }

      return Object.values(this.getDemandBreakdown(record)).reduce((sum, line) => (
        sum + (parseFloat(line?.kw) || 0)
      ), 0);
    },

    getDemandUnit(record = {}) {
      return this.isTimeOfUseDemandRecord(record) ? 'kW' : 'kVA';
    },

    // Helper method to get calculated total from either old or new format
    getCalculatedTotal(record) {
      if (!record.calculatedCharges) return 0;

      // New dynamic format
      if (record.calculatedCharges.totalCalculated !== undefined) {
        return record.calculatedCharges.totalCalculated;
      }

      // Old format fallback
      if (record.calculatedCharges.kwhCharges !== undefined) {
        return (record.calculatedCharges.kwhCharges || 0)
               + (record.calculatedCharges.kvaCharges || 0)
               + (record.calculatedCharges.serviceCharges || 0)
               + (record.calculatedCharges.SERVICE_CHARGE || 0)
               + (record.calculatedCharges.vatAmount || 0)
               + (record.calculatedCharges.reaAmount || 0)
               + (record.calculatedCharges.ewuraAmount || 0);
      }

      return Object.entries(record.calculatedCharges).reduce((total, [key, value]) => {
        if (key !== 'totalCalculated' && typeof value === 'number') {
          return total + value;
        }
        return total;
      }, 0);
    },

    // Calculate per unit charge (actual bill amount / total consumption)
    getPerUnitCharge(record) {
      const consumption = record.consumptionData?.totalConsumption || 0;
      const actualAmount = record.actualBillAmount || 0;

      if (consumption === 0) return 0;

      return actualAmount / consumption;
    },
  },
};
</script>

<style scoped>
.filter-card-title {
  min-height: 48px;
  padding: 10px 16px 6px;
}

.filter-card-text {
  padding: 8px 16px 12px;
}

.filter-actions {
  align-items: center;
  display: flex;
}

.table-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 112px;
  white-space: nowrap;
}

.table-action-btn {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
